import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { useCallback } from 'react';
import {
  type AbsenceTimeline,
  type AbsenceTimelineDto,
  selectTimeline,
} from '../../lib/blocks';
import { absenceScopeUrl } from './constants';

export type AbsenceScope = {
  timeline: AbsenceTimeline;
  /**
   * Days the server could not place. Approving a withdrawal deletes the
   * absence it was about, so a reference to a day that is gone is routine --
   * and has to read differently from a day that has merely moved.
   */
  unresolved: number[];
};

type AbsenceScopeDto = AbsenceTimelineDto & { unresolved?: number[] };

/**
 * The blocks and entries behind named days, whole, whatever the calendar holds.
 *
 * The window the timeline draws is not the shape of an absence: the pane can
 * name days that have since scrolled out of it, and an edit worked out from
 * the visible fragment would leave the rest of the entry where it was. This
 * asks about the days themselves instead.
 *
 * It replaces guessing at how far to reach -- a year either side, then three,
 * then nine -- with one request naming what is actually wanted. Membership and
 * the revision are the server's answer, so there is nothing left to infer from
 * how much came back.
 */
export default function useAbsenceScope() {
  const apiClient = useAPIClient();

  return useCallback(
    async (absenceIds: readonly number[]): Promise<AbsenceScope | null> => {
      const ids = [...new Set(absenceIds)];
      if (!ids.length) return null;
      try {
        const dto = (await apiClient.request({
          url: absenceScopeUrl,
          payload: { ids: ids.join(',') },
        })) as AbsenceScopeDto;
        return {
          timeline: selectTimeline(dto),
          unresolved: dto.unresolved ?? [],
        };
      } catch {
        // Nothing to add to what the caller has, and nothing it can do with
        // half an answer: it stops and says so rather than submitting.
        return null;
      }
    },
    [apiClient],
  );
}
